/**
 * The citizen's three answers to a company's request for more (D2): Share,
 * Not now, Decline — each the invitation's own endpoint, which the server
 * routes to the relationship the request widens.
 *
 *   • Share   → accept: the consent widens, on the trail; the company now sees
 *               what it asked for. No journey is chosen or created. A request
 *               for more TESTS whose every test is already banked is complete
 *               the moment it is shared (D3): the citizen lands on TestIt with
 *               the answer — the company and the results it now has — which
 *               the page confirms, exactly as for a TestIt invitation.
 *   • Not now → defer: out of Home for the snooze window, still in the bell.
 *   • Decline → terminal; nothing widens and the company is told.
 *
 * A failed answer reports the server's CODE in the citizen's language, never
 * a server message.
 */
import { useTranslation } from '@tonnie/locale';
import { translateInviteError } from '@tonnie/onboarding';
import { useAcceptInvitation, useDeclineInvitation, useDeferInvitation } from '@tonnie/sdk';
import { useCallback } from 'react';
import { toast } from 'sonner';
import { useLocation } from 'wouter';

import { testitSharedState } from '@/features/platform/first-run';

export function useShareRequestAnswer(token: string, company: string) {
  const { t } = useTranslation();
  const accept = useAcceptInvitation();
  const decline = useDeclineInvitation();
  const defer = useDeferInvitation();
  const [, navigate] = useLocation();

  const failed = useCallback(
    (error: unknown) => {
      const copy = translateInviteError(t, (error as { code?: string } | null)?.code);
      toast.error(copy.title, { description: copy.body });
    },
    [t]
  );

  const share = useCallback(
    (onDone?: () => void) =>
      accept.mutate(
        { token },
        {
          onSuccess: (result) => {
            // Nothing left to take: TestIt confirms what was shared — the
            // surface that took the answer is leaving the page with it.
            if (result.testit?.completed) {
              navigate('/testit', {
                state: testitSharedState({ company, tests: result.testit.tests }),
              });
              return;
            }
            toast.success(t('invitations.request.sharedToast', { company }), {
              description: t('invitations.request.sharedToastBody'),
            });
            onDone?.();
          },
          onError: failed,
        }
      ),
    [accept, token, t, company, failed, navigate]
  );

  const notNow = useCallback(
    (onDone?: () => void) => defer.mutate(token, { onSuccess: onDone, onError: failed }),
    [defer, token, failed]
  );

  const refuse = useCallback(
    (onDone?: () => void) =>
      decline.mutate(token, {
        onSuccess: () => {
          toast.success(t('invitations.request.declinedToast', { company }));
          onDone?.();
        },
        onError: failed,
      }),
    [decline, token, t, company, failed]
  );

  return {
    share,
    notNow,
    decline: refuse,
    busy: accept.isPending || decline.isPending || defer.isPending,
    sharing: accept.isPending,
  };
}
